import { useEffect, useState, useRef, useMemo } from "react";
import { db } from "../firebaseConfig";
import { 
  collection, 
  getDocs, 
  updateDoc, 
  doc, 
  getDoc, 
  query, 
  where, 
  arrayUnion, 
  deleteDoc,
  orderBy,
  limit,
  startAfter,
  getCountFromServer,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";

const iconUrl = (t: any) =>
  t?.icon || t?.sampleImageUrl || t?.sampleMedia?.primary?.[0] || "";

// ─────────────────────────────────────────────────────────────────────────────
// Types for multi-photo per-role approvals
// ─────────────────────────────────────────────────────────────────────────────
type MediaRole = 'primary' | 'expiration_label' | 'after';

interface RoleApproval {
  status: 'approved' | 'rejected' | 'pending';
  reviewedAt?: number;
  reviewerId?: string;
  rejectReason?: string;
}

type ApprovalByRole = Partial<Record<MediaRole, RoleApproval>>;

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Determine if submission is multi-photo
// Detection priority:
//   1. submission.media array has 2+ roles
//   2. task.taskKind !== 'single'
// ─────────────────────────────────────────────────────────────────────────────
function isMultiPhotoSubmission(submission: any): boolean {
  if (!submission) return false;
  // Method 1: Check if submission has media array with multiple roles
  if (Array.isArray(submission.media) && submission.media.length >= 2) {
    return true;
  }
  // Method 2: Check task's taskKind
  const taskKind = submission.task?.taskKind;
  if (taskKind && taskKind !== 'single') {
    return true;
  }
  return false;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Get required roles for a submission
// Returns ['primary'] for single-photo, or ['primary', secondaryRole] for multi
// ─────────────────────────────────────────────────────────────────────────────
function getRequiredRoles(submission: any): MediaRole[] {
  if (!submission) return ['primary'];
  if (!isMultiPhotoSubmission(submission)) {
    return ['primary'];
  }
  
  const taskKind = submission.task?.taskKind;
  
  // Determine secondary role based on taskKind or media array
  let secondaryRole: MediaRole | null = null;
  
  if (taskKind === 'expiration') {
    secondaryRole = 'expiration_label';
  } else if (taskKind === 'before_after') {
    secondaryRole = 'after';
  } else if (Array.isArray(submission.media)) {
    // Fallback: check what's in media array
    const roles = submission.media.map((m: any) => m.role);
    if (roles.includes('expiration_label')) {
      secondaryRole = 'expiration_label';
    } else if (roles.includes('after')) {
      secondaryRole = 'after';
    }
  }
  
  return secondaryRole ? ['primary', secondaryRole] : ['primary'];
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Get image URL for a specific role
// ─────────────────────────────────────────────────────────────────────────────
function getImageUrlForRole(submission: any, role: MediaRole): string | null {
  if (!submission) return null;
  if (role === 'primary') {
    // Primary always uses mediaUrl for backward compatibility
    return submission.mediaUrl || null;
  }
  
  // For secondary roles, look in media array
  if (Array.isArray(submission.media)) {
    const mediaItem = submission.media.find((m: any) => m.role === role);
    return mediaItem?.url || null;
  }
  
  return null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Check if all required roles are approved
// ─────────────────────────────────────────────────────────────────────────────
function areAllRolesApproved(approvalByRole: ApprovalByRole, requiredRoles: MediaRole[]): boolean {
  return requiredRoles.every(role => approvalByRole[role]?.status === 'approved');
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Get display label for a role
// ─────────────────────────────────────────────────────────────────────────────
function getRoleDisplayLabel(role: MediaRole, taskKind?: string): string {
  if (role === 'primary') {
    if (taskKind === 'before_after') return 'Before';
    return 'Primary';
  }
  if (role === 'expiration_label') return 'Expiration Label';
  if (role === 'after') return 'After';
  return role;
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper: Check if submission is expiration-kind
// ─────────────────────────────────────────────────────────────────────────────
function isExpirationKindSubmission(submission: any): boolean {
  if (!submission) return false;
  const taskKind = submission.task?.taskKind;
  const informationType = submission.task?.informationType || submission.informationType;
  return taskKind === 'expiration' || informationType === 'Expiration Date';
}

type ApprovalTab = 'photo' | 'video';

export default function Approvals() {
  const [submissions, setSubmissions] = useState<any[]>([]);
  const [selected, setSelected] = useState<any | null>(null);
  const [otherSubmissions, setOtherSubmissions] = useState<any[]>([]);
  const [loadingOtherSubmissions, setLoadingOtherSubmissions] = useState(false);
  const [previewVideoUrl, setPreviewVideoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const imageBoxRef = useRef<HTMLDivElement>(null);
  const rightColumnRef = useRef<HTMLDivElement>(null);
  const [rightColumnHeight, setRightColumnHeight] = useState<number | null>(null);

  // Queue filters
  const [filters, setFilters] = useState({
    category: 'all',
    subcategory: 'all',
    itemType: 'all',
    task: 'all',
  });

  useEffect(() => {
    document.title = 'Task Approvals — Pixano';
  }, []);
  
  // ─────────────────────────────────────────────────────────────────────────
  // Tab state
  // ─────────────────────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState<ApprovalTab>('photo');
  
  // ─────────────────────────────────────────────────────────────────────────
  // Multi-photo role selection state
  // ─────────────────────────────────────────────────────────────────────────
  const [activeRole, setActiveRole] = useState<MediaRole>('primary');
  
  // Rejection reason modal state
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState("");
  const [customRejectReason, setCustomRejectReason] = useState("");
  const [rejectingSubmission, setRejectingSubmission] = useState<any | null>(null);
  
  // QA Review modal state
  const [showQAModal, setShowQAModal] = useState(false);
  const [qaReason, setQaReason] = useState("");
  const [qaReviewingSubmission, setQaReviewingSubmission] = useState<any | null>(null);
  
  // Expiration gating: itemType selection state
  const [itemType, setItemType] = useState("");
  
  // Video trimming state
  const [trimStartSec, setTrimStartSec] = useState<number>(0);
  const [trimEndSec, setTrimEndSec] = useState<number>(10);
  const [videoDuration, setVideoDuration] = useState<number>(0);
  const [trimming, setTrimming] = useState(false);

  // Predefined rejection reasons
  const rejectionReasons = [
    "Wrong item photographed",
    "Image quality too poor",
    "Doesn't meet task criteria", 
    "Inappropriate content",
    "Duplicate submission",
    "Other"
  ];

  // QA Review reasons for Task Approvals
  const qaReviewReasons = [
    "Not Sure if Task is Correct",
    "Image Quality Questionable",
    "Questionable Approval",
    "inappropriate",
    "Possible Duplicate",
    "Looks like AI",
    "Looks like Computer Screen"
  ];

  // Reset activeRole when selected submission changes
  useEffect(() => {
    setActiveRole('primary');
  }, [selected?.id]);
  
  // Reset itemType when selected submission changes
  useEffect(() => {
    if (selected) {
      // Pre-populate with existing itemType if present
      setItemType(selected.itemType || '');
    } else {
      setItemType('');
    }
  }, [selected?.id]);
  
  // Reset trim state when selected video changes
  useEffect(() => {
    if (selected && activeTab === 'video') {
      setTrimStartSec(0);
      setTrimEndSec(10);
      setVideoDuration(0);
      setTrimming(false);
    }
  }, [selected?.id, activeTab]);

  useEffect(() => {
    let cancelled = false;

    const loadOtherSubmissions = async () => {
      if (activeTab !== 'video' || !selected?.userId || !selected?.taskId) {
        setOtherSubmissions([]);
        setLoadingOtherSubmissions(false);
        return;
      }

      setLoadingOtherSubmissions(true);
      try {
        const userRef = doc(db, "users", selected.userId);
        const userSnap = await getDoc(userRef);
        if (cancelled) return;

        if (userSnap.exists()) {
          const userData = userSnap.data();
          const completedTasks = userData.completedTasks || {};
          const taskData = completedTasks[selected.taskId];
          const allSubs =
            taskData && Array.isArray(taskData.submissions) ? taskData.submissions : [];
          const filtered = allSubs.filter(
            (item: any) => item.submissionId !== selected.id && item.id !== selected.id
          );
          setOtherSubmissions(filtered);
        } else {
          setOtherSubmissions([]);
        }
      } catch (error) {
        console.error('Error loading other submissions:', error);
        setOtherSubmissions([]);
      } finally {
        if (!cancelled) {
          setLoadingOtherSubmissions(false);
        }
      }
    };

    loadOtherSubmissions();
    return () => {
      cancelled = true;
    };
  }, [activeTab, selected?.id, selected?.userId, selected?.taskId]);

  // Pagination state
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot<DocumentData> | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  // Build query for current tab
  const buildQuery = (tab: ApprovalTab, startAfterDoc?: QueryDocumentSnapshot<DocumentData> | null) => {
    const baseConstraints: any[] = [
      where('status', 'in', ['pending', 'flagged']),
      orderBy('createdAt', 'asc')
    ];

    if (startAfterDoc) {
      baseConstraints.push(startAfter(startAfterDoc));
    }

    baseConstraints.push(limit(50));

    if (tab === 'photo') {
      return query(
        collection(db, "submissions"),
        where('status', 'in', ['pending', 'flagged']),
        where('taskType', '==', 'Photo'),
        orderBy('createdAt', 'asc'),
        ...(startAfterDoc ? [startAfter(startAfterDoc)] : []),
        limit(50)
      );
    } else if (tab === 'video') {
      return query(
        collection(db, "submissions"),
        where('status', 'in', ['pending', 'flagged']),
        where('taskType', '==', 'Video'),
        where('videoStage', '==', 'approval'),
        orderBy('createdAt', 'asc'),
        ...(startAfterDoc ? [startAfter(startAfterDoc)] : []),
        limit(50)
      );
    }
    
    // Fallback (should not happen)
    return query(
      collection(db, "submissions"),
      where('status', 'in', ['pending', 'flagged']),
      orderBy('createdAt', 'asc'),
      ...(startAfterDoc ? [startAfter(startAfterDoc)] : []),
      limit(50)
    );
  };

  // Fetch submissions for current tab
  useEffect(() => {
    const fetchSubmissions = async () => {
      setLoading(true);
      setLastDoc(null);
      setHasMore(true);
      
      try {
        const q = buildQuery(activeTab);
        const submissionsSnap = await getDocs(q);
        
        if (submissionsSnap.empty) {
          setSubmissions([]);
          setSelected(null);
          setHasMore(false);
          setLoading(false);
          return;
        }

        // Fetch task data for each submission
        const submissionsWithTask = await Promise.all(
          submissionsSnap.docs.map(async (sub) => {
            const subData = sub.data();
            const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
            return {
              id: sub.id,
              ...subData,
              task: taskSnap.exists() ? taskSnap.data() : null
            };
          })
        );

        setSubmissions(submissionsWithTask);
        setSelected(submissionsWithTask[0] || null);
        
        // Update pagination state
        if (submissionsSnap.docs.length < 50) {
          setHasMore(false);
        } else {
          setLastDoc(submissionsSnap.docs[submissionsSnap.docs.length - 1]);
        }
      } catch (error) {
        console.error('Error fetching submissions:', error);
        setSubmissions([]);
        setSelected(null);
      } finally {
        setLoading(false);
      }
    };
    fetchSubmissions();
  }, [activeTab]);

  // Load more submissions
  const handleLoadMore = async () => {
    if (!hasMore || loadingMore || !lastDoc) return;

    setLoadingMore(true);
    try {
      const q = buildQuery(activeTab, lastDoc);
      const submissionsSnap = await getDocs(q);

      if (submissionsSnap.empty) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      // Fetch task data for new submissions
      const newSubmissionsWithTask = await Promise.all(
        submissionsSnap.docs.map(async (sub) => {
          const subData = sub.data();
          const taskSnap = await getDoc(doc(db, "tasks", subData.taskId));
          return {
            id: sub.id,
            ...subData,
            task: taskSnap.exists() ? taskSnap.data() : null
          };
        })
      );

      setSubmissions(prev => [...prev, ...newSubmissionsWithTask]);
      
      // Update pagination state
      if (submissionsSnap.docs.length < 50) {
        setHasMore(false);
      } else {
        setLastDoc(submissionsSnap.docs[submissionsSnap.docs.length - 1]);
      }
    } catch (error) {
      console.error('Error loading more submissions:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  // Measure image box height and constrain right column to match
  useEffect(() => {
    const updateHeights = () => {
      if (imageBoxRef.current && rightColumnRef.current) {
        const imageBoxHeight = imageBoxRef.current.offsetHeight;
        setRightColumnHeight(imageBoxHeight);
      }
    };

    updateHeights();
    
    // Update on window resize
    window.addEventListener('resize', updateHeights);
    
    // Update when selected changes (image might load and change height)
    const timer1 = setTimeout(updateHeights, 100);
    const timer2 = setTimeout(updateHeights, 500);
    const timer3 = setTimeout(updateHeights, 1000);
    
    // Also update when images load
    if (imageBoxRef.current) {
      const images = imageBoxRef.current.querySelectorAll('img');
      images.forEach(img => {
        if (img.complete) {
          updateHeights();
        } else {
          img.addEventListener('load', updateHeights);
        }
      });
    }
    
    return () => {
      window.removeEventListener('resize', updateHeights);
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
      if (imageBoxRef.current) {
        const images = imageBoxRef.current.querySelectorAll('img');
        images.forEach(img => {
          img.removeEventListener('load', updateHeights);
        });
      }
    };
  }, [selected, loading, activeRole]);

  // ─────────────────────────────────────────────────────────────────────────
  // ROLE-AWARE handleAction
  // ─────────────────────────────────────────────────────────────────────────
  const handleAction = async (status: "approved" | "rejected", rejectReason?: string) => {
    if (!selected) return;
    
    // For expiration-kind submissions, require itemType before approving
    if (status === "approved" && isExpirationKindSubmission(selected)) {
      if (!itemType || !itemType.trim()) {
        alert("Please select an Item Type before approving this expiration submission.");
        return;
      }
    }
    
    try {
      const isMultiPhoto = isMultiPhotoSubmission(selected);
      const requiredRoles = getRequiredRoles(selected);
      const currentApprovalByRole: ApprovalByRole = selected.approvalByRole || {};
      
      // Build the role approval update
      const roleApproval: RoleApproval = {
        status,
        reviewedAt: Date.now(),
        reviewerId: "admin"
      };
      if (status === "rejected" && rejectReason) {
        roleApproval.rejectReason = rejectReason;
      }
      
      // Merge into approvalByRole
      const updatedApprovalByRole: ApprovalByRole = {
        ...currentApprovalByRole,
        [activeRole]: roleApproval
      };
      
      // Determine overall status
      let overallStatus: "approved" | "rejected" | "pending" | "flagged" = selected.status;
      let shouldRemoveFromQueue = false;
      
      if (status === "rejected") {
        // ANY role rejection → overall rejected immediately
        overallStatus = "rejected";
        shouldRemoveFromQueue = true;
      } else if (status === "approved") {
        // Check if ALL required roles are now approved
        if (areAllRolesApproved(updatedApprovalByRole, requiredRoles)) {
          overallStatus = "approved";
          shouldRemoveFromQueue = true;
          console.log(`✅ [Approvals] All ${requiredRoles.length} required roles approved for submission ${selected.id}`);
        } else {
          // Not all roles approved yet - keep in queue
          overallStatus = selected.status; // Keep pending/flagged
          shouldRemoveFromQueue = false;
          console.log(`⏳ [Approvals] Role '${activeRole}' approved, but not all roles complete yet. Required: ${requiredRoles.join(', ')}`);
        }
      }

      // ─────────────────────────────────────────────────────────────────────
      // Prepare update data for submission doc
      // ─────────────────────────────────────────────────────────────────────
      const updateData: any = {
        approvalByRole: updatedApprovalByRole,
        adminReviewedAt: Date.now(),
        adminReviewedBy: "admin"
      };

      // For expiration-kind submissions, set itemType and itemTypeStage on approve
      if (status === "approved" && isExpirationKindSubmission(selected)) {
        updateData.itemType = itemType.trim();
        updateData.itemTypeStage = 'provisional';
      }
      
      // Only set overall status if we're actually changing it
      if (shouldRemoveFromQueue) {
        updateData.status = overallStatus;
        
        if (overallStatus === "approved") {
        updateData.approvedAt = Date.now();
        console.log(`✅ [Approvals] Approving submission ${selected.id} - will appear in workbench queue if task has detection enabled`);
      }

        if (overallStatus === "rejected" && rejectReason) {
        updateData.rejectReason = rejectReason;
        updateData.rejectedAt = Date.now();
        updateData.rejectedBy = "admin";
        }
      }

      // Update the submission document
      console.log(`📝 [Approvals] Updating submission ${selected.id} - role: ${activeRole}, roleStatus: ${status}, overallStatus: ${overallStatus}`);
      await updateDoc(doc(db, "submissions", selected.id), updateData);
      console.log(`✅ [Approvals] Submission ${selected.id} updated successfully in submissions collection`);
      
      // Log task info for debugging workbench queue
      if (overallStatus === "approved" && selected.task) {
        const hasDetection = selected.task.detection?.enabled && selected.task.detection?.schemaRef?.id;
        console.log(`🔍 [Approvals] Task ${selected.taskId} detection info:`, {
          enabled: selected.task.detection?.enabled,
          hasSchema: !!selected.task.detection?.schemaRef?.id,
          schemaId: selected.task.detection?.schemaRef?.id,
          willAppearInWorkbench: hasDetection
        });
      }

      // ─────────────────────────────────────────────────────────────────────
      // Update user's completedTasks (mirror the changes)
      // ─────────────────────────────────────────────────────────────────────
      const userRef = doc(db, "users", selected.userId);
      const userSnap = await getDoc(userRef);
      if (userSnap.exists()) {
        const userData = userSnap.data();
        const completedTasks = userData.completedTasks || {};
        const taskData = completedTasks[selected.taskId];
        
        if (taskData && Array.isArray(taskData.submissions)) {
          // Update the specific submission in the user's completedTasks
          // Match by submissionId, id, or mediaUrl to handle different data formats
          const updatedSubmissions = taskData.submissions.map((sub: any) => {
            const matches = 
              sub.submissionId === selected.id || 
              sub.id === selected.id ||
              (sub.mediaUrl && selected.mediaUrl && sub.mediaUrl === selected.mediaUrl);
            
            if (matches) {
              const submissionUpdate: any = {
                ...sub, // Preserve all existing fields (including media[], answers, etc.)
                approvalByRole: updatedApprovalByRole,
                adminReviewedAt: Date.now(),
                adminReviewedBy: "admin"
              };
              
              // Ensure submissionId is set if it wasn't before
              if (!submissionUpdate.submissionId) {
                submissionUpdate.submissionId = selected.id;
              }
              
              // Only update overall status if we're removing from queue
              if (shouldRemoveFromQueue) {
                submissionUpdate.status = overallStatus;
                
                if (overallStatus === "rejected" && rejectReason) {
                submissionUpdate.rejectReason = rejectReason;
                submissionUpdate.rejectedAt = Date.now();
                submissionUpdate.rejectedBy = "admin";
                }
              }
              
              return submissionUpdate;
            }
            return sub;
          });
          
          // Get reward from task (needed for approved submissions)
          const reward = Number(selected.task?.reward) || Number(selected.reward) || 0;
          
          // Update the task-level status
          const updatedTaskData = {
            ...taskData,
            submissions: updatedSubmissions,
            adminReviewedAt: Date.now(),
            adminReviewedBy: "admin"
          };
          
          // Only update task-level status if removing from queue
          if (shouldRemoveFromQueue) {
            updatedTaskData.status = overallStatus;
            
            if (overallStatus === "rejected" && rejectReason) {
            updatedTaskData.rejectReason = rejectReason;
            updatedTaskData.rejectedAt = Date.now();
            updatedTaskData.rejectedBy = "admin";
            }
          }
          
          // Apply cooldown and PIX reward ONLY for fully approved submissions
          if (overallStatus === "approved" && shouldRemoveFromQueue) {
            // Apply cooldown by updating lastSubmittedAt
            updatedTaskData.lastSubmittedAt = Date.now();
            
            // Ensure reward is saved in the matching submission object
            const finalUpdatedSubmissions = updatedSubmissions.map((sub: any) => {
              const matches = 
                sub.submissionId === selected.id || 
                sub.id === selected.id ||
                (sub.mediaUrl && selected.mediaUrl && sub.mediaUrl === selected.mediaUrl);
              
              if (matches && sub.status === 'approved') {
                return {
                  ...sub,
                  reward: reward, // Always set reward for approved submissions
                  rewardAmount: reward // Also add rewardAmount for compatibility
                };
              }
              return sub;
            });
            
            // Update the submissions array with rewards
            updatedTaskData.submissions = finalUpdatedSubmissions;
            
            // Also set reward at task level if not already set
            if (reward > 0 && !updatedTaskData.reward) {
              updatedTaskData.reward = reward;
            }
            
            if (reward > 0) {
              // Combine both updates into a single call to avoid partial failures
            await updateDoc(userRef, { 
                pixBalance: arrayUnion(reward),
                [`completedTasks.${selected.taskId}`]: updatedTaskData
              });
              console.log(`✅ Applied cooldown and ${reward} PIX reward for approved submission (from task: ${selected.task?.reward})`);
            } else {
              // No reward, just update completedTasks
              await updateDoc(userRef, {
                [`completedTasks.${selected.taskId}`]: updatedTaskData
              });
              console.log(`✅ Applied cooldown for approved submission (no reward - task reward: ${selected.task?.reward})`);
            }
            
            // Warn if task doesn't have detection enabled (won't appear in workbench queue)
            if (!selected.task?.detection?.enabled || !selected.task?.detection?.schemaRef?.id) {
              console.warn(`⚠️ [Approvals] WARNING: Task ${selected.taskId} does not have detection enabled or schema assigned. Submission will NOT appear in workbench queue.`);
              console.warn(`⚠️ [Approvals] Task detection status:`, {
                enabled: selected.task?.detection?.enabled,
                hasSchema: !!selected.task?.detection?.schemaRef?.id,
                schemaId: selected.task?.detection?.schemaRef?.id
              });
          } else {
              console.log(`✅ [Approvals] Task ${selected.taskId} has detection enabled - submission should appear in workbench queue`);
            }
          } else if (overallStatus === "rejected" && shouldRemoveFromQueue) {
            // For rejected submissions, don't apply cooldown - allow immediate retry
            await updateDoc(userRef, {
              [`completedTasks.${selected.taskId}`]: updatedTaskData
            });
            console.log(`❌ Submission rejected - no cooldown applied, user can retry immediately`);
          } else {
            // Partial approval - still in queue, just update the approvalByRole
          await updateDoc(userRef, {
            [`completedTasks.${selected.taskId}`]: updatedTaskData
          });
            console.log(`⏳ Partial role approval saved to user history`);
          }
        }
      }

      // ─────────────────────────────────────────────────────────────────────
      // Update local state
      // ─────────────────────────────────────────────────────────────────────
      if (shouldRemoveFromQueue) {
      // Remove from the list and select the next submission
      const updatedSubmissions = submissions.filter(p => p.id !== selected.id);
      setSubmissions(updatedSubmissions);
      setSelected(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);
        setActiveRole('primary'); // Reset role for next submission
        // Refresh counts
        const [pCount, vCount] = await Promise.all([
          getTabCount('photo'),
          getTabCount('video')
        ]);
        setPhotoCount(pCount);
        setVideoCount(vCount);
      } else {
        // Update the submission in place (partial approval)
        const updatedSubmissions = submissions.map(p => {
          if (p.id === selected.id) {
            return {
              ...p,
              approvalByRole: updatedApprovalByRole
            };
          }
          return p;
        });
        setSubmissions(updatedSubmissions);
        setSelected({
          ...selected,
          approvalByRole: updatedApprovalByRole
        });
        
        // Auto-advance to next unapproved role
        const nextUnapprovedRole = requiredRoles.find(
          role => updatedApprovalByRole[role]?.status !== 'approved'
        );
        if (nextUnapprovedRole && nextUnapprovedRole !== activeRole) {
          setActiveRole(nextUnapprovedRole);
        }
      }
      
      // Close rejection modal if it was open
      if (status === "rejected") {
        setShowRejectModal(false);
        setRejectReason("");
        setCustomRejectReason("");
        setRejectingSubmission(null);
      }
    } catch (error: any) {
      console.error("Error updating submission:", error);
      console.error("Error details:", {
        code: error?.code,
        message: error?.message,
        stack: error?.stack
      });
      
      // Show a warning but don't block the UI
      alert(`Error updating submission: ${error?.message || 'Unknown error'}. Please try again.`);
    }
  };

  // Handle opening rejection modal
  const handleRejectClick = () => {
    setRejectingSubmission(selected);
    setShowRejectModal(true);
    setRejectReason("");
    setCustomRejectReason("");
  };

  // Handle rejection reason selection
  const handleRejectReasonChange = (reason: string) => {
    setRejectReason(reason);
    if (reason !== "Other") {
      setCustomRejectReason("");
    }
  };

  // Handle final rejection submission
  const handleRejectSubmit = () => {
    const finalReason = rejectReason === "Other" ? customRejectReason : rejectReason;
    if (!finalReason.trim()) {
      alert("Please provide a rejection reason.");
      return;
    }
    handleAction("rejected", finalReason);
  };

  // Handle rejection modal close
  const handleRejectModalClose = () => {
    setShowRejectModal(false);
    setRejectReason("");
    setCustomRejectReason("");
    setRejectingSubmission(null);
  };


  const getStatusBadge = (status: string) => {
    if (status === "flagged") {
      return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">Flagged</span>;
    }
    return <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded text-xs">Pending</span>;
  };

  const getConfidenceBadge = (confidence: string) => {
    if (confidence === "low") {
      return <span className="bg-red-100 text-red-800 px-2 py-1 rounded text-xs">⚠️ Low Confidence</span>;
    }
    return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs">✓ High Confidence</span>;
  };

  const getSimilarityInfo = (submission: any) => {
    if (submission.validationDetails?.similarity) {
      return (
        <div className="mb-2">
          <span className="font-semibold">Similarity Score: </span>
          <span className={`${submission.validationDetails.similarity >= 0.6 ? 'text-green-600' : 'text-yellow-600'}`}>
            {(submission.validationDetails.similarity * 100).toFixed(1)}%
          </span>
        </div>
      );
    }
    return null;
  };

  const getLabelingInfo = (submission: any) => {
    if (!submission.labelingDetails) return null;

    const details = submission.labelingDetails;
    return (
      <div className="mb-4 p-3 bg-gray-50 rounded border">
        <h4 className="font-semibold mb-2">Labeling Details</h4>
        <div className="mb-2">
          <span className="font-semibold">Match Confidence: </span>
          {getConfidenceBadge(details.matchConfidence)}
        </div>
        <div className="mb-2">
          <span className="font-semibold">Labels: </span>
          <span className="text-sm text-gray-600">
            {submission.labels?.join(', ') || 'None'}
          </span>
        </div>
        <div className="mb-2">
          <span className="font-semibold">Expected Keywords: </span>
          <span className="text-sm text-gray-600">
            {details.verificationDetails?.expectedKeywords?.join(', ') || 'None'}
          </span>
        </div>
        <div className="mb-2">
          <span className="font-semibold">Matched Keywords: </span>
          <span className="text-sm text-gray-600">
            {details.verificationDetails?.matchedKeywords?.join(', ') || 'None'}
          </span>
        </div>
        <div className="text-xs text-gray-500">
          Match: {details.verificationDetails?.matchCount || 0} / {details.verificationDetails?.totalExpected || 0}
        </div>
      </div>
    );
  };

  const handleQAClick = () => {
    setQaReviewingSubmission(selected);
    setShowQAModal(true);
    setQaReason("");
  };

  const handleQAReasonChange = (reason: string) => {
    setQaReason(reason);
  };

  const handleQASubmit = async () => {
    if (!qaReason.trim()) {
      alert("Please select a QA Review reason.");
      return;
    }
    
    if (!qaReviewingSubmission) return;
    
    try {
      await updateDoc(doc(db, "submissions", qaReviewingSubmission.id), {
        qaReviewStatus: 'pending',
        qaReviewReason: qaReason,
        qaReviewSentAt: Date.now(),
        qaReviewSentBy: 'admin'
      });

      // Remove from approvals list
      const updatedSubmissions = submissions.filter(p => p.id !== qaReviewingSubmission.id);
      setSubmissions(updatedSubmissions);
      setSelected(updatedSubmissions.length > 0 ? updatedSubmissions[0] : null);
      
      // Refresh counts
      const [pCount, vCount] = await Promise.all([
        getTabCount('photo'),
        getTabCount('video')
      ]);
      setPhotoCount(pCount);
      setVideoCount(vCount);
      
      // Close modal
      setShowQAModal(false);
      setQaReason("");
      setQaReviewingSubmission(null);
      
      alert('Submission sent to QA Review!');
    } catch (error) {
      console.error("Error sending to QA Review:", error);
      alert("Error updating submission. Please try again.");
    }
  };

  const handleQAModalClose = () => {
    setShowQAModal(false);
    setQaReason("");
    setQaReviewingSubmission(null);
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Helper: Get role approval status badge
  // ─────────────────────────────────────────────────────────────────────────
  const getRoleStatusBadge = (submission: any, role: MediaRole) => {
    const roleApproval = submission.approvalByRole?.[role];
    if (!roleApproval) {
      return <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">Pending</span>;
    }
    if (roleApproval.status === 'approved') {
      return <span className="bg-green-100 text-green-800 px-2 py-0.5 rounded text-xs">✓ Approved</span>;
    }
    if (roleApproval.status === 'rejected') {
      return <span className="bg-red-100 text-red-800 px-2 py-0.5 rounded text-xs">✗ Rejected</span>;
    }
    return <span className="bg-gray-100 text-gray-600 px-2 py-0.5 rounded text-xs">Pending</span>;
  };

  // ─────────────────────────────────────────────────────────────────────────
  // Computed values for current submission
  // ─────────────────────────────────────────────────────────────────────────
  const isMultiPhoto = selected ? isMultiPhotoSubmission(selected) : false;
  const requiredRoles: MediaRole[] = selected ? getRequiredRoles(selected) : ['primary'];
  const currentImageUrl = selected ? (
    activeTab === 'video'
      ? (selected.mediaUrl || selected.videoUrl || null)
      : getImageUrlForRole(selected, activeRole)
  ) : null;
  const currentRoleApproval = selected?.approvalByRole?.[activeRole];
  const isCurrentRoleAlreadyReviewed = currentRoleApproval?.status === 'approved' || currentRoleApproval?.status === 'rejected';

  // Count submissions by tab using Firestore count aggregation
  const getTabCount = async (tab: ApprovalTab): Promise<number> => {
    try {
      let countQuery;
      
      if (tab === 'photo') {
        countQuery = query(
          collection(db, "submissions"),
          where('status', 'in', ['pending', 'flagged']),
          where('taskType', '==', 'Photo')
        );
      } else if (tab === 'video') {
        countQuery = query(
          collection(db, "submissions"),
          where('status', 'in', ['pending', 'flagged']),
          where('taskType', '==', 'Video'),
          where('videoStage', '==', 'approval')
        );
      } else {
        return 0;
      }

      const snapshot = await getCountFromServer(countQuery);
      return snapshot.data().count;
    } catch (error) {
      console.error(`Error getting count for tab ${tab}:`, error);
      return 0;
    }
  };

  const [photoCount, setPhotoCount] = useState<number>(0);
  const [videoCount, setVideoCount] = useState<number>(0);

  useEffect(() => {
    const updateCounts = async () => {
      const [pCount, vCount] = await Promise.all([
        getTabCount('photo'),
        getTabCount('video')
      ]);
      setPhotoCount(pCount);
      setVideoCount(vCount);
    };
    updateCounts();
    // Update counts when tab changes or every 10 seconds
    const interval = setInterval(updateCounts, 10000);
    return () => clearInterval(interval);
  }, [activeTab]);

  // ---- Filter option lists, derived from the currently loaded queue ----
  const subCategory = (s: any) => s?.task?.category || '';
  const subSubcategory = (s: any) => s?.task?.subcategory || '';
  const subItemType = (s: any) => s?.itemType || s?.task?.itemType || '';

  const uniqueCategories = useMemo(
    () => [...new Set(submissions.map(subCategory).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [submissions]
  );

  const uniqueSubcategories = useMemo(() => {
    const pool =
      filters.category === 'all'
        ? submissions
        : submissions.filter((s: any) => subCategory(s) === filters.category);
    return [...new Set(pool.map(subSubcategory).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [submissions, filters.category]);

  const uniqueItemTypes = useMemo(() => {
    const pool = submissions
      .filter((s: any) => filters.category === 'all' || subCategory(s) === filters.category)
      .filter((s: any) => filters.subcategory === 'all' || subSubcategory(s) === filters.subcategory);
    return [...new Set(pool.map(subItemType).filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [submissions, filters.category, filters.subcategory]);

  const uniqueTasks = useMemo(() => {
    const pool = submissions
      .filter((s: any) => filters.category === 'all' || subCategory(s) === filters.category)
      .filter((s: any) => filters.subcategory === 'all' || subSubcategory(s) === filters.subcategory)
      .filter((s: any) => filters.itemType === 'all' || subItemType(s) === filters.itemType);
    const map = new Map<string, string>();
    pool.forEach((s: any) => {
      if (s.taskId) map.set(s.taskId, s.task?.title || s.taskId);
    });
    return [...map.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [submissions, filters.category, filters.subcategory, filters.itemType]);

  // ---- The filtered queue ----
  const filteredSubmissions = useMemo(() => {
    return submissions.filter((s: any) => {
      if (filters.category !== 'all' && subCategory(s) !== filters.category) return false;
      if (filters.subcategory !== 'all' && subSubcategory(s) !== filters.subcategory) return false;
      if (filters.itemType !== 'all' && subItemType(s) !== filters.itemType) return false;
      if (filters.task !== 'all' && s.taskId !== filters.task) return false;
      return true;
    });
  }, [submissions, filters]);

  // ---- Keep the selected submission consistent with the filtered queue ----
  useEffect(() => {
    if (filteredSubmissions.length === 0) {
      if (selected !== null) setSelected(null);
      return;
    }
    const stillVisible = selected && filteredSubmissions.some((s: any) => s.id === selected.id);
    if (!stillVisible) {
      setSelected(filteredSubmissions[0]);
    }
  }, [filteredSubmissions]);

  return (
    <div className="max-w-[1400px] mx-auto">
      {/* Header: Title and tabs */}
      <div className="border-b mb-4 pb-2">
        <h1 className="text-3xl font-bold mb-4">Task Approvals</h1>
        
        {/* Tabs */}
        <div className="flex gap-2 border-b">
          <button
            onClick={() => setActiveTab('photo')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'photo'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Photo Approvals ({photoCount})
          </button>
          <button
            onClick={() => setActiveTab('video')}
            className={`px-4 py-2 font-medium transition-colors ${
              activeTab === 'video'
                ? 'border-b-2 border-blue-600 text-blue-600'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Video Approvals ({videoCount})
          </button>
        </div>
      </div>

      {/* Queue Filters */}
      <div className="mb-4 grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Category</label>
          <select
            value={filters.category}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, category: e.target.value, subcategory: 'all', itemType: 'all', task: 'all' }))
            }
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Categories</option>
            {uniqueCategories.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Subcategory</label>
          <select
            value={filters.subcategory}
            onChange={(e) =>
              setFilters((prev) => ({ ...prev, subcategory: e.target.value, itemType: 'all', task: 'all' }))
            }
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Subcategories</option>
            {uniqueSubcategories.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Item Type</label>
          <select
            value={filters.itemType}
            onChange={(e) => setFilters((prev) => ({ ...prev, itemType: e.target.value, task: 'all' }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Item Types</option>
            {uniqueItemTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Task</label>
          <select
            value={filters.task}
            onChange={(e) => setFilters((prev) => ({ ...prev, task: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Tasks</option>
            {uniqueTasks.map(([id, title]) => (
              <option key={id} value={id}>{title}</option>
            ))}
          </select>
        </div>
        <div>
          <button
            type="button"
            onClick={() => setFilters({ category: 'all', subcategory: 'all', itemType: 'all', task: 'all' })}
            className="w-full px-3 py-1.5 text-sm rounded bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
          >
            Clear Filters
          </button>
        </div>
      </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 items-start">
          {/* LEFT COLUMN: concise details + image */}
          <div className="lg:col-span-3 border p-4 rounded" id="image-box" ref={imageBoxRef}>
            {loading ? (
              <div>Loading...</div>
            ) : selected ? (
              <div>
                {/* Task name on its own row (larger) */}
                  <div className="mb-2">
                  <div className="flex items-center justify-between text-xl">
                    <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Task:</span>
                    <span className="text-gray-900">{selected.task?.title || '-'}</span>
                      {/* Multi-photo indicator */}
                      {isMultiPhoto && (
                        <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs ml-2">
                          Multi-Photo ({requiredRoles.length} images)
                        </span>
                      )}
                  </div>
                    <button
                      onClick={handleQAClick}
                      disabled={!selected}
                      className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50"
                      title="Send to QA Review for review"
                    >
                      QA Review
                    </button>
                </div>
                </div>
                <div className="mb-2 flex items-center gap-6 text-base">
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Category:</span>
                    <span className="text-gray-900">{selected.task?.category || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Subcategory:</span>
                    <span className="text-gray-900">{selected.task?.subcategory || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Item Type:</span>
                    <span className="text-gray-900">{(selected as any)?.itemType || '-'}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-700 font-bold">Language:</span>
                    <span className="text-gray-900">{selected.contentLanguageLabel || selected.contentLanguageCode || '-'}</span>
                  </div>
                </div>

                {activeTab === 'video' && selected.videoMetadata && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">Video Source Metadata</div>
                    <div className="space-y-1">
                      <div className="flex items-center space-x-2 text-sm">
                        <span className={selected.videoMetadata.isMetaGlasses ? 'text-green-600' : 'text-red-500'}>
                          {selected.videoMetadata.isMetaGlasses ? '✅' : '❌'}
                        </span>
                        <span className="text-gray-600">
                          Device: {selected.videoMetadata.isMetaGlasses
                            ? 'Ray-Ban Meta Smart Glasses'
                            : (selected.videoMetadata.deviceModel || 'Unknown')}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm">
                        <span className={selected.videoMetadata.isMetaGlasses ? 'text-green-600' : 'text-red-500'}>
                          {selected.videoMetadata.isMetaGlasses ? '✅' : '❌'}
                        </span>
                        <span className="text-gray-600">
                          Copyright: {selected.videoMetadata.isMetaGlasses
                            ? 'Meta AI'
                            : (selected.videoMetadata.copyright || 'Unknown')}
                        </span>
                      </div>
                      {selected.videoMetadata.width > 0 && selected.videoMetadata.height > 0 && (
                        <div className="flex items-center space-x-2 text-sm">
                          <span className={selected.videoMetadata.isMetaGlasses ? 'text-green-600' : 'text-red-500'}>
                            {selected.videoMetadata.isMetaGlasses ? '✅' : '❌'}
                          </span>
                          <span className="text-gray-600">
                            Dimensions: {selected.videoMetadata.width}×{selected.videoMetadata.height}
                          </span>
                        </div>
                      )}
                      {selected.videoMetadata.duration > 0 && (
                        <div className="text-sm text-gray-600">
                          ⏱ Duration: {selected.videoMetadata.duration.toFixed(1)}s
                        </div>
                      )}
                      {selected.videoMetadata.fileSize > 0 && (
                        <div className="text-sm text-gray-600">
                          💾 File size: {(selected.videoMetadata.fileSize / (1024 * 1024)).toFixed(1)} MB
                        </div>
                      )}
                    </div>
                  </div>
                )}


                {activeTab === 'video' && !selected.videoMetadata && (
                  <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                    <div className="text-sm text-yellow-700">
                      ⚠️ No video source metadata available for this submission
                    </div>
                  </div>
                )}

                {getSimilarityInfo(selected)}

                {/* ─────────────────────────────────────────────────────────────────────
                    ROLE SELECTOR (only for multi-photo submissions)
                ───────────────────────────────────────────────────────────────────── */}
                {isMultiPhoto && (
                  <div className="mb-3 p-3 bg-gray-50 rounded border">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold text-sm">Review Image:</span>
                      <div className="flex gap-2">
                        {requiredRoles.map((role: MediaRole) => (
                          <button
                            key={role}
                            onClick={() => setActiveRole(role)}
                            className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                              activeRole === role
                                ? 'bg-blue-600 text-white'
                                : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            {getRoleDisplayLabel(role, selected.task?.taskKind)}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-4 text-sm">
                      {requiredRoles.map((role: MediaRole) => (
                        <div key={role} className="flex items-center gap-1">
                          <span className="text-gray-600">{getRoleDisplayLabel(role, selected.task?.taskKind)}:</span>
                          {getRoleStatusBadge(selected, role)}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Media display - image for photos, video for videos */}
                <div className="mb-4">
                  {(activeTab === 'photo' || activeTab === 'video') && currentImageUrl ? (
                    <>
                      {isMultiPhoto && activeTab === 'photo' && (
                        <div className="text-sm text-gray-600 mb-1">
                          Showing: <strong>{getRoleDisplayLabel(activeRole, selected.task?.taskKind)}</strong> image
                        </div>
                      )}
                      {activeTab === 'video' && (
                        <div className="text-sm text-gray-600 mb-1">
                          Video Submission
                        </div>
                      )}
                      {activeTab === 'photo' ? (
                        <img
                          src={currentImageUrl}
                          alt={`Submission - ${activeRole}`}
                          className="mt-1 w-full rounded border object-contain bg-white"
                          style={{ maxHeight: 'clamp(420px, calc(100vh - 280px), 820px)' }}
                          onError={e => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = "/placeholder.png";
                          }}
                        />
                      ) : (
                        <div className="mt-1 w-full rounded border bg-black" style={{ maxHeight: 'clamp(420px, calc(100vh - 280px), 820px)' }}>
                          <video
                            src={currentImageUrl}
                            controls
                            className="w-full"
                            style={{ maxHeight: 'clamp(420px, calc(100vh - 280px), 820px)' }}
                            onLoadedMetadata={(e) => {
                              const video = e.currentTarget;
                              setVideoDuration(video.duration);
                              if (trimEndSec === 10 && video.duration > 10) {
                                setTrimEndSec(Math.min(video.duration, 30));
                              }
                            }}
                          />
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex flex-col items-center justify-center mt-2 p-8 bg-gray-50 rounded border">
                      <img
                        src="/placeholder.png"
                        alt="No submission"
                        className="max-w-xs rounded border opacity-50"
                        style={{ maxHeight: 150 }}
                      />
                      <span className="text-gray-500 text-sm mt-2">
                        {activeTab === 'photo' 
                          ? `No image available for ${getRoleDisplayLabel(activeRole, selected?.task?.taskKind)} role`
                          : 'No video available'}
                      </span>
                    </div>
                  )}
                </div>

                {activeTab === 'video' && selected && (
                  <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                    <div className="text-sm font-semibold text-gray-700 mb-2">
                      This user&apos;s other submissions for this task
                    </div>
                    {loadingOtherSubmissions ? (
                      <div className="text-sm text-gray-600">Loading…</div>
                    ) : otherSubmissions.length === 0 ? (
                      <div className="text-sm text-gray-500">No other submissions for this task.</div>
                    ) : (
                      <div className="flex gap-3 overflow-x-auto pb-2">
                        {otherSubmissions.map((item: any, index: number) => {
                          const curDur = selected.videoMetadata?.duration;
                          const itemDur = item.videoMetadata?.duration;
                          const isPossibleDuplicate =
                            curDur != null &&
                            itemDur != null &&
                            Math.abs(curDur - itemDur) <= 1;
                          const status = item.status || 'pending';
                          const statusBadgeClass =
                            status === 'approved'
                              ? 'bg-green-100 text-green-800'
                              : status === 'rejected'
                              ? 'bg-red-100 text-red-800'
                              : status === 'flagged'
                              ? 'bg-yellow-100 text-yellow-800'
                              : 'bg-blue-100 text-blue-800';
                          const cardVideoUrl =
                            item.mediaUrl || item.videoUrl || item.trimmedVideoUrl || '';
                          return (
                            <div
                              key={item.submissionId || item.id || index}
                              className="flex-shrink-0 w-40 border rounded p-2 bg-white cursor-pointer"
                              onClick={() =>
                                setPreviewVideoUrl(
                                  item.mediaUrl || item.videoUrl || item.trimmedVideoUrl || null
                                )
                              }
                            >
                              {cardVideoUrl ? (
                                <div className="relative w-full h-24 rounded overflow-hidden bg-black">
                                  <video
                                    src={`${cardVideoUrl}#t=0.1`}
                                    muted
                                    playsInline
                                    preload="metadata"
                                    className="w-full h-24 object-cover"
                                    style={{ pointerEvents: 'none' }}
                                  />
                                  <div className="absolute inset-0 flex items-center justify-center text-white text-lg pointer-events-none">
                                    ▶
                                  </div>
                                </div>
                              ) : (
                                <div className="w-full h-24 bg-black/80 rounded flex items-center justify-center text-white text-xs">
                                  ▶ View
                                </div>
                              )}
                              {item.videoMetadata?.duration != null && (
                                <div className="text-xs text-gray-600 mt-1">
                                  {item.videoMetadata.duration}s
                                </div>
                              )}
                              <span
                                className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${statusBadgeClass}`}
                              >
                                {status}
                              </span>
                              {isPossibleDuplicate && (
                                <div className="text-xs text-amber-700 mt-1 font-medium">
                                  ⚠️ Possible duplicate
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ) : (
              <div>No submission selected.</div>
            )}
          </div>
          {/* RIGHT COLUMN: validation prompt + actions, then queue */}
          <div 
            className="lg:col-span-1 flex flex-col overflow-hidden" 
            ref={rightColumnRef}
            style={rightColumnHeight ? { maxHeight: `${rightColumnHeight}px` } : {}}
          >
            <div className="border p-4 rounded flex-shrink-0">
              {/* Sent to QA Reason section */}
              {selected?.qaReviewReason && (
                <div className="mb-4 p-3 bg-orange-50 border border-orange-200 rounded">
                  <h4 className="font-semibold text-sm text-orange-900 mb-1">Sent to QA Reason</h4>
                  <p className="text-sm text-orange-800">{selected.qaReviewReason}</p>
                </div>
              )}
              
              {/* Current role approval status (for multi-photo) */}
              {isMultiPhoto && currentRoleApproval && (
                <div className={`mb-4 p-3 rounded border ${
                  currentRoleApproval.status === 'approved' 
                    ? 'bg-green-50 border-green-200' 
                    : 'bg-red-50 border-red-200'
                }`}>
                  <h4 className={`font-semibold text-sm mb-1 ${
                    currentRoleApproval.status === 'approved' ? 'text-green-900' : 'text-red-900'
                  }`}>
                    {getRoleDisplayLabel(activeRole, selected?.task?.taskKind)} Already Reviewed
                  </h4>
                  <p className={`text-sm ${
                    currentRoleApproval.status === 'approved' ? 'text-green-800' : 'text-red-800'
                  }`}>
                    Status: {currentRoleApproval.status === 'approved' ? '✓ Approved' : '✗ Rejected'}
                    {currentRoleApproval.rejectReason && (
                      <span className="block mt-1">Reason: {currentRoleApproval.rejectReason}</span>
                    )}
                  </p>
                </div>
              )}
              
              {/* Task reference image */}
              {iconUrl(selected?.task) && (
                <div className="mb-4">
                  <img
                    src={iconUrl(selected?.task)}
                    alt="Task example"
                    className="w-[100px] h-[100px] object-contain rounded border border-gray-200 bg-gray-50"
                  />
                </div>
              )}

              <h3 className="font-semibold mb-3 text-lg">
                {isMultiPhoto 
                  ? `Review: ${getRoleDisplayLabel(activeRole, selected?.task?.taskKind)}`
                  : 'Validation Prompt'
                }
              </h3>
              <div className="text-base text-gray-900 min-h-[48px] whitespace-pre-wrap">
                {selected?.validationDetails?.prompt || (selected as any)?.validationPrompt || selected?.task?.validationPrompt || "-"}
            </div>

              {/* Expiration gating: ItemType selection for expiration-kind submissions */}
              {isExpirationKindSubmission(selected) && (
                <div className="mt-4 mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Item Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={itemType}
                    onChange={(e) => setItemType(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  >
                    <option value="">Select Item Type...</option>
                    <option value="Food">Food</option>
                    <option value="Beverage">Beverage</option>
                    <option value="Medicine">Medicine</option>
                    <option value="Cosmetic">Cosmetic</option>
                    <option value="Other">Other</option>
                  </select>
                  {!itemType && (
                    <p className="text-xs text-red-600 mt-1">Item Type is required for expiration submissions</p>
                  )}
        </div>
      )}

              {/* AI Signals */}
              <div className="mt-4 mb-4 p-3 bg-gray-50 border border-gray-200 rounded-lg">
                <h4 className="text-sm font-semibold text-gray-700 mb-2">AI Signal</h4>
                {(selected as any)?.aiSignals ? (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Score</span>
                      <span className={`text-sm font-medium ${
                        (selected as any).aiSignals.clipScore >= 0.7
                          ? 'text-green-600'
                          : (selected as any).aiSignals.clipScore >= 0.4
                          ? 'text-yellow-600'
                          : 'text-red-600'
                      }`}>
                        {((selected as any).aiSignals.clipScore * 100).toFixed(0)}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">Recommendation</span>
                      <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                        (selected as any).aiSignals.recommendation === 'approve'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-yellow-100 text-yellow-800'
                      }`}>
                        {(selected as any).aiSignals.recommendation === 'approve' ? 'Approve' : 'Review'}
                      </span>
                    </div>
                    {(selected as any).aiSignals.model && (
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Model</span>
                        <span className="text-xs text-gray-600">{(selected as any).aiSignals.model}</span>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-gray-400">—</p>
                )}
              </div>

              {/* Action buttons - different for each tab */}
              {activeTab === 'photo' && (
                <div className="flex items-center gap-3 mt-4">
                  <button 
                    className={`px-4 py-2 rounded ${
                      isCurrentRoleAlreadyReviewed 
                        ? 'bg-gray-300 cursor-not-allowed' 
                        : 'bg-red-500 text-white hover:bg-red-600'
                    }`} 
                    onClick={handleRejectClick}
                    disabled={isCurrentRoleAlreadyReviewed}
                    title={isCurrentRoleAlreadyReviewed ? 'This role has already been reviewed' : `Reject ${isMultiPhoto ? getRoleDisplayLabel(activeRole, selected?.task?.taskKind) : 'submission'}`}
                  >
                    Reject{isMultiPhoto ? ` ${getRoleDisplayLabel(activeRole, selected?.task?.taskKind)}` : ''}
                  </button>
                  <button 
                    className={`px-4 py-2 rounded ${
                      isCurrentRoleAlreadyReviewed || (isExpirationKindSubmission(selected) && !itemType?.trim())
                        ? 'bg-gray-300 cursor-not-allowed' 
                        : 'bg-green-500 text-white hover:bg-green-600'
                    }`} 
                    onClick={() => handleAction("approved")}
                    disabled={isCurrentRoleAlreadyReviewed || (isExpirationKindSubmission(selected) && !itemType?.trim())}
                    title={
                      isCurrentRoleAlreadyReviewed 
                        ? 'This role has already been reviewed' 
                        : isExpirationKindSubmission(selected) && !itemType?.trim()
                          ? 'Please select Item Type before approving'
                          : `Approve ${isMultiPhoto ? getRoleDisplayLabel(activeRole, selected?.task?.taskKind) : 'submission'}`
                    }
                  >
                    Approve{isMultiPhoto ? ` ${getRoleDisplayLabel(activeRole, selected?.task?.taskKind)}` : ''}
                  </button>
                </div>
              )}
              
              {activeTab === 'video' && (
                <div className="flex items-center gap-3 mt-4">
                  <button 
                    className="px-4 py-2 rounded bg-red-500 text-white hover:bg-red-600"
                    onClick={handleRejectClick}
                    title="Reject video submission"
                  >
                    Reject
                  </button>
                  <button 
                    className="px-4 py-2 rounded bg-blue-500 text-white hover:bg-blue-600"
                    onClick={async () => {
                      if (!selected) return;
                      try {
                        // 1. Update submissions collection
                        await updateDoc(doc(db, "submissions", selected.id), {
                          videoStage: 'trimming',
                          status: 'approved',
                          approvedAt: Date.now(),
                          adminReviewedAt: Date.now(),
                          adminReviewedBy: "admin"
                        });

                        // 2. Update users collection (mirror handleAction photo approve path)
                        const userRef = doc(db, "users", selected.userId);
                        const userSnap = await getDoc(userRef);
                        if (userSnap.exists()) {
                          const userData = userSnap.data();
                          const completedTasks = userData.completedTasks || {};
                          const taskData = completedTasks[selected.taskId];
                          if (taskData && Array.isArray(taskData.submissions)) {
                            const reward = Number(selected.task?.reward) || Number(selected.reward) || 0;

                            // First pass — update status on matching submission
                            const updatedSubmissions = taskData.submissions.map((sub: any) => {
                              const isMatch =
                                sub.submissionId === selected.id ||
                                sub.id === selected.id ||
                                sub.mediaUrl === selected.mediaUrl;
                              if (!isMatch) return sub;
                              return {
                                ...sub,
                                submissionId: sub.submissionId || selected.id,
                                status: 'approved',
                                adminReviewedAt: Date.now(),
                                adminReviewedBy: "admin",
                              };
                            });

                            // Second pass — add reward to matching submission
                            const finalSubmissions = reward > 0
                              ? updatedSubmissions.map((sub: any) => {
                                  const isMatch =
                                    sub.submissionId === selected.id ||
                                    sub.id === selected.id ||
                                    sub.mediaUrl === selected.mediaUrl;
                                  if (!isMatch || sub.status !== 'approved') return sub;
                                  return { ...sub, reward, rewardAmount: reward };
                                })
                              : updatedSubmissions;

                            const updatedTaskData = {
                              ...taskData,
                              submissions: finalSubmissions,
                              status: 'approved',
                              adminReviewedAt: Date.now(),
                              adminReviewedBy: "admin",
                              lastSubmittedAt: Date.now(),
                              ...(reward > 0 && !taskData.reward ? { reward } : {}),
                            };

                            const userUpdatePayload: any = {
                              [`completedTasks.${selected.taskId}`]: updatedTaskData,
                            };
                            if (reward > 0) {
                              userUpdatePayload.pixBalance = arrayUnion(reward);
                            }

                            await updateDoc(userRef, userUpdatePayload);
                          }
                        }

                        // 3. Local UI updates
                        const updatedSubmissions2 = submissions.filter(p => p.id !== selected.id);
                        setSubmissions(updatedSubmissions2);
                        setSelected(updatedSubmissions2.length > 0 ? updatedSubmissions2[0] : null);
                        const [pCount, vCount] = await Promise.all([
                          getTabCount('photo'),
                          getTabCount('video'),
                        ]);
                        setPhotoCount(pCount);
                        setVideoCount(vCount);
                        alert('Video approved!');
                      } catch (error: any) {
                        console.error('Error approving video:', error);
                        alert('Error updating submission. Please try again.');
                      }
                    }}
                    title="Approve video"
                  >
                    Approve
                  </button>
                  <button 
                    className="px-4 py-2 rounded bg-orange-600 text-white hover:bg-orange-700"
                    onClick={handleQAClick}
                    title="Send to QA Review"
                  >
                    Send to QA
                  </button>
                </div>
              )}
              
              {/* Progress indicator for multi-photo */}
              {isMultiPhoto && (
                <div className="mt-3 text-sm text-gray-600">
                  <span className="font-medium">Progress:</span>{' '}
                  {requiredRoles.filter(r => selected?.approvalByRole?.[r]?.status === 'approved').length} / {requiredRoles.length} roles approved
              </div>
            )}
          </div>

            <div className="border p-4 rounded flex-1 flex flex-col mt-4 overflow-hidden" style={{ minHeight: 0, maxHeight: '100%' }}>
            <h3 className="font-semibold mb-2 flex-shrink-0">Review Queue ({filteredSubmissions.length}{filteredSubmissions.length !== submissions.length ? ` of ${submissions.length}` : ''})</h3>
            <ul className="overflow-y-auto flex-1 pr-2" style={{ minHeight: 0 }}>
              {filteredSubmissions.map(p => (
                <li
                  key={p.id}
                  className={`p-2 mb-2 rounded cursor-pointer border ${selected?.id === p.id ? "bg-blue-100 border-blue-300" : "hover:bg-gray-100"}`}
                  onClick={() => setSelected(p)}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="font-medium flex items-center gap-1">
                        {p.task?.title || "Unknown"}
                        {isMultiPhotoSubmission(p) && (
                          <span className="bg-purple-100 text-purple-700 px-1 rounded text-xs">Multi</span>
                        )}
                    </div>
                      <div className="text-sm text-gray-600">Category: {p.task?.category || "-"}</div>
                      {p.contentLanguageLabel && (
                        <span className="text-xs text-gray-500 ml-1">
                          · {p.contentLanguageLabel}
                        </span>
                      )}
                  </div>
                    {getStatusBadge(p.status)}
                  </div>
                  {p.validationDetails?.similarity && (
                    <div className="text-xs text-gray-500 mt-1">
                      Score: {(p.validationDetails.similarity * 100).toFixed(1)}%
                    </div>
                  )}
                  {/* Show partial approval progress for multi-photo */}
                  {isMultiPhotoSubmission(p) && p.approvalByRole && (
                    <div className="text-xs text-purple-600 mt-1">
                      Roles: {getRequiredRoles(p).filter(r => p.approvalByRole?.[r]?.status === 'approved').length}/{getRequiredRoles(p).length} approved
                    </div>
                  )}
                </li>
              ))}
            </ul>
            {hasMore && (
              <div className="mt-4 flex justify-center">
                <button
                  onClick={handleLoadMore}
                  disabled={loadingMore}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
                >
                  {loadingMore ? 'Loading...' : 'Load More'}
                </button>
              </div>
            )}
          </div>
        </div>
        </div>

      {/* Rejection Reason Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">
                Reject {isMultiPhoto ? getRoleDisplayLabel(activeRole, selected?.task?.taskKind) : 'Submission'}
              </h2>
              <button onClick={handleRejectModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {rejectingSubmission && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {rejectingSubmission.task?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>User:</strong> {rejectingSubmission.userId}
                </p>
                {isMultiPhoto && (
                  <p className="text-purple-600 mb-2">
                    <strong>Image Role:</strong> {getRoleDisplayLabel(activeRole, selected?.task?.taskKind)}
                  </p>
                )}
              </div>
            )}
            
            {isMultiPhoto && (
              <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded">
                <p className="text-sm text-yellow-800">
                  ⚠️ Rejecting any role will reject the entire submission.
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select Rejection Reason:
              </label>
              <select 
                value={rejectReason} 
                onChange={(e) => handleRejectReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                <option value="">Choose a reason...</option>
                {rejectionReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            
            {rejectReason === "Other" && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Custom Reason:
                </label>
                <textarea
                  value={customRejectReason}
                  onChange={(e) => setCustomRejectReason(e.target.value)}
                  placeholder="Please provide a specific reason for rejection..."
                  className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-red-500"
                  rows={3}
                />
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleRejectModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleRejectSubmit}
                disabled={!rejectReason || (rejectReason === "Other" && !customRejectReason.trim())}
                className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Reject {isMultiPhoto ? getRoleDisplayLabel(activeRole, selected?.task?.taskKind) : 'Submission'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QA Review Modal */}
      {showQAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold">Send to QA Review</h2>
              <button onClick={handleQAModalClose} className="text-gray-500 hover:text-gray-700 text-2xl">&times;</button>
            </div>
            
            {qaReviewingSubmission && (
              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  <strong>Task:</strong> {qaReviewingSubmission.task?.title || "Unknown"}
                </p>
                <p className="text-gray-600 mb-2">
                  <strong>User:</strong> {qaReviewingSubmission.userId}
                </p>
              </div>
            )}
            
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select QA Review Reason:
              </label>
              <select 
                value={qaReason} 
                onChange={(e) => handleQAReasonChange(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Choose a reason...</option>
                {qaReviewReasons.map((reason) => (
                  <option key={reason} value={reason}>{reason}</option>
                ))}
              </select>
            </div>
            
            <div className="flex gap-2 justify-end">
              <button
                onClick={handleQAModalClose}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleQASubmit}
                disabled={!qaReason.trim()}
                className="px-4 py-2 bg-orange-500 text-white rounded hover:bg-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                Send to QA Review
              </button>
            </div>
          </div>
        </div>
      )}

      {previewVideoUrl && (
        <div
          className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center"
          onClick={() => setPreviewVideoUrl(null)}
        >
          <div
            className="bg-white rounded-lg p-4 w-full mx-4"
            style={{ maxWidth: '800px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={previewVideoUrl}
              controls
              autoPlay
              style={{ maxHeight: '70vh', width: '100%' }}
            />
            <button
              type="button"
              className="mt-3 px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
              onClick={() => setPreviewVideoUrl(null)}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
  